import { Platform } from 'react-native';
import { observeRtcEvent, withEntryAttempt } from './entryAttempt';
import { storage } from '../utils/storage';
import { API_BASE_URL as BASE_URL } from './apiUrl';
import { installNativeBackgroundFlush } from './nativeBackgroundFlush';
import { NOOP_RECONFIG_EVENT } from './rtc/noopReconfiguration';
import { absorbSdkOrphanRejection } from './rtc/publicationAbort';

/**
 * Technical telemetry for Agora RTC sessions (live broadcasts + practice
 * rooms). Separate from the analytics pipeline because:
 *   - Analytics events are *product* signals (page-views, clicks) sent to
 *     /api/analytics/events and used for funnels — keeping RTC noise out of
 *     that table avoids polluting product dashboards.
 *   - RTC telemetry is *engineering* signal — Agora subscribe failures,
 *     publish failures, JS errors mid-broadcast — needed to debug
 *     "audio works but no video" reports without asking users to open
 *     DevTools. Stored in a separate table so engineers can query freely.
 *
 * Reliability guarantees:
 *   - Queue survives reload/crash. Every push synchronously persists the
 *     pending queue to localStorage (web only); on next mount we read it
 *     back and continue flushing.
 *   - Idempotent. Each event gets a client-generated id; the BE has a
 *     unique index on it and ignores duplicates. Re-sending the same batch
 *     after a network blip (or after picking up persisted events post-crash)
 *     is safe.
 *   - Last-mile flush via navigator.sendBeacon on beforeunload, so events
 *     buffered in the last few seconds before a tab close still ship.
 *
 * Native platforms (CORE-474): the native live room runs its own RTC stack
 * (react-native-agora / LiveKit native), so telemetry IS captured there too.
 * The web-specific reliability features stay web-gated — localStorage queue
 * persistence, navigator.sendBeacon last-mile flush, and the window/document
 * unload/visibility + global-error listeners are all no-ops on native. Native
 * keeps an in-memory queue flushed on the same interval via fetch (which works
 * in React Native); a cold app kill drops at most one interval's worth of
 * buffered events, an acceptable trade vs. an AsyncStorage write per push.
 */

const STORAGE_KEY = 'agape_rtc_telemetry_queue_v1';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 50;
const MAX_QUEUE_SIZE = 500;
const ENDPOINT = '/api/telemetry/rtc-events';

export type RtcSurface = 'master' | 'viewer' | 'room';

/**
 * Which RTC backend this session runs on. CORE-1046: the column existed since
 * CORE-394, but the value never reached it — call sites put `provider` inside
 * `properties`, while the BE reads a top-level field. Result: 55 226 of 55 227
 * events with a NULL Provider, an empty `byProvider` breakdown in the admin API,
 * and no way to compare connect-success between backends — which is exactly the
 * gate the CORE-395 rollout stands on.
 */
export type RtcProviderName = 'agora' | 'livekit';

export interface RtcContext {
    surface: RtcSurface;
    rtcSessionId: string;
    liveId?: number;
    roomId?: number;
    role?: string | null;
    uid?: number | null;
    /**
     * Set once per session by whoever owns the context — for provider-specific
     * hooks that is the hook itself, for the dual-path surfaces (AgoraViewer,
     * MasterLivePublisher) it is assigned when the token resolves. Contexts are
     * held by reference, so a later assignment stamps subsequent events too.
     */
    provider?: RtcProviderName | null;
}

interface PersistedEvent {
    /** Client-generated id; BE unique index dedupes on this. */
    clientEventId: string;
    eventName: string;
    surface: RtcSurface;
    provider: string | null;
    rtcSessionId: string;
    liveId: number | null;
    roomId: number | null;
    role: string | null;
    uid: number | null;
    pageUrl: string | null;
    timestampUtc: string;
    properties: Record<string, any>;
}

function isWeb(): boolean {
    return Platform.OS === 'web' && typeof window !== 'undefined';
}

/**
 * Whether telemetry is captured at all. True on web (with a real window) and on
 * native. Web-only reliability features (localStorage, sendBeacon, window/
 * document listeners) keep the stricter isWeb() gate; this broader gate governs
 * the shared emit/queue/flush path so native (CORE-474) participates.
 */
function isEnabled(): boolean {
    return Platform.OS === 'web' ? typeof window !== 'undefined' : true;
}

function genId(): string {
    // 16-byte URL-safe id. Sufficient entropy for dedupe; doesn't need uuid.
    if (isWeb() && typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
        return (crypto as any).randomUUID();
    }
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let s = '';
    for (let i = 0; i < 24; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return `${Date.now().toString(36)}-${s}`;
}

function readPersistedQueue(): PersistedEvent[] {
    if (!isWeb()) return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
}

function writePersistedQueue(queue: PersistedEvent[]): void {
    if (!isWeb()) return;
    try {
        // Cap before persisting — drop OLDEST on overflow so freshest debug
        // info wins. RTC errors that have been queued for hours are usually
        // less interesting than the one that just happened.
        const trimmed = queue.length > MAX_QUEUE_SIZE
            ? queue.slice(queue.length - MAX_QUEUE_SIZE)
            : queue;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
        // Quota exceeded etc. — don't crash the app over telemetry.
    }
}

class RtcTelemetryClient {
    private queue: PersistedEvent[] = [];
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private isFlushing = false;

    constructor() {
        if (!isEnabled()) return;

        // Resume from any pending events left by a previous tab/session (web
        // only — native keeps the queue in memory).
        this.queue = readPersistedQueue();

        this.flushTimer = setInterval(() => { this.flush().catch(() => { }); }, FLUSH_INTERVAL_MS);
        // CORE-1957 — native keeps the queue in memory only: send it before the
        // app may be killed in the background.
        installNativeBackgroundFlush(() => { this.flushNow(); });

        if (isWeb()) {
            // Last-ditch flush on tab close. fetch() is unreliable mid-unload;
            // sendBeacon is the documented escape hatch.
            window.addEventListener('beforeunload', () => {
                this.flushBeacon();
            });
            // visibilitychange covers tab-switch + mobile-web background, where
            // beforeunload often doesn't fire on Safari/Chrome mobile.
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.flushBeacon();
                }
            });
        }

        // First flush attempt soon after startup so persisted-from-last-session
        // events ship immediately rather than waiting a full interval.
        if (this.queue.length > 0) {
            setTimeout(() => { this.flush().catch(() => { }); }, 250);
        }
    }

    push(event: PersistedEvent) {
        if (!isEnabled()) return;
        this.queue.push(event);
        // Cap the in-memory queue (drop oldest) so native — which has no
        // localStorage cap in writePersistedQueue — can't grow unbounded if the
        // network is down for a long session. Web is additionally capped on
        // persist below.
        if (this.queue.length > MAX_QUEUE_SIZE) {
            this.queue = this.queue.slice(this.queue.length - MAX_QUEUE_SIZE);
        }
        writePersistedQueue(this.queue); // web-only; no-op on native
    }

    /** CORE-1957 — an out-of-cycle send (room left, app backgrounded). */
    flushNow() {
        this.flush().catch(() => { });
    }

    private async flush() {
        if (!isEnabled() || this.queue.length === 0 || this.isFlushing) return;
        this.isFlushing = true;
        const batch = this.queue.slice(0, MAX_BATCH_SIZE);
        try {
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            const token = await storage.getToken();
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(`${BASE_URL}${ENDPOINT}`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ events: batch }),
            });

            if (res.ok || res.status === 204) {
                // Successfully accepted — drop only the batch we sent. Items
                // pushed during the await stay queued for the next flush.
                this.queue = this.queue.slice(batch.length);
                writePersistedQueue(this.queue);
            } else if (res.status === 400 || res.status === 413) {
                // Bad request / payload too large — these will never succeed
                // on retry. Drop the batch so we don't loop forever.
                this.queue = this.queue.slice(batch.length);
                writePersistedQueue(this.queue);
            }
            // 5xx / network errors: leave the queue intact, try again next tick.
        } catch {
            // Network error — keep the batch in the queue for the next flush.
        } finally {
            this.isFlushing = false;
        }
    }

    /**
     * Synchronous-ish flush via sendBeacon. Used during page unload when
     * fetch() may be cancelled. Beacon is fire-and-forget — we optimistically
     * remove the events from localStorage so the next page load doesn't
     * re-send them.
     */
    private flushBeacon() {
        if (!isWeb() || this.queue.length === 0) return;
        if (typeof navigator.sendBeacon !== 'function') return;
        try {
            const batch = this.queue.slice(0, MAX_BATCH_SIZE);
            const blob = new Blob(
                [JSON.stringify({ events: batch })],
                { type: 'application/json' },
            );
            const ok = navigator.sendBeacon(`${BASE_URL}${ENDPOINT}`, blob);
            if (ok) {
                this.queue = this.queue.slice(batch.length);
                writePersistedQueue(this.queue);
            }
        } catch { /* swallow */ }
    }
}

const client = new RtcTelemetryClient();

/** CORE-1957 — send the queued RTC events now instead of on the next tick. */
export function flushRtcTelemetry(): void {
    client.flushNow();
}

// ── Active-context registry for global JS error tagging ─────────────────
let errorListenersInstalled = false;
const activeContexts = new Set<RtcContext>();

function installErrorListeners() {
    if (errorListenersInstalled) return;
    if (!isWeb()) return;
    errorListenersInstalled = true;

    window.addEventListener('error', (e: ErrorEvent) => {
        if (activeContexts.size === 0) return;
        for (const ctx of activeContexts) {
            try {
                emit('rtc_js_error', ctx, {
                    message: e.message,
                    filename: e.filename,
                    lineno: e.lineno,
                    colno: e.colno,
                    stack: typeof e.error?.stack === 'string' ? e.error.stack.slice(0, 2000) : undefined,
                });
            } catch { /* never let telemetry crash the app */ }
        }
    });

    // CORE-2001 — a no-op setConfiguration the browser refused and we answered
    // as applied (Chrome 155 macOS + Agora SDK). Counted per active session so
    // the workaround's reach is visible in RTC telemetry, not only in breadcrumbs.
    window.addEventListener(NOOP_RECONFIG_EVENT, (e: Event) => {
        if (activeContexts.size === 0) return;
        const detail = (e as CustomEvent).detail ?? {};
        for (const ctx of activeContexts) {
            try {
                emit('rtc_pc_setconfig_noop_absorbed', ctx, { ...detail });
            } catch { /* swallow */ }
        }
    });

    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
        // CORE-1900 — the Agora SDK's own orphaned rejections (WS_ABORT /
        // OPERATION_ABORTED / its republish's CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS)
        // are marked handled before anything else, also after the room is gone.
        let absorbed = false;
        try { absorbed = absorbSdkOrphanRejection(e); } catch { /* swallow */ }
        if (activeContexts.size === 0) return;
        const reason: any = e.reason;
        const message = typeof reason === 'string'
            ? reason
            : (reason && typeof reason.message === 'string' ? reason.message : String(reason));
        const stack = reason && typeof reason.stack === 'string' ? reason.stack.slice(0, 2000) : undefined;
        for (const ctx of activeContexts) {
            try {
                emit('rtc_js_unhandled_rejection', ctx, {
                    message,
                    stack,
                    name: reason?.name,
                    code: reason?.code,
                    absorbed,
                });
            } catch { /* swallow */ }
        }
    });
}

function emit(eventName: string, ctx: RtcContext, properties: Record<string, any>) {
    if (!isEnabled()) return;
    // CORE-1884 — the RTC stage of the current entry attempt.
    observeRtcEvent(eventName);
    client.push({
        clientEventId: genId(),
        eventName,
        surface: ctx.surface,
        // Context first; `properties.provider` stays a valid per-event override
        // so the call sites that already pass it (and any future one-off) keep
        // stamping the column without a context.
        provider: ctx.provider
            ?? (typeof properties.provider === 'string' ? properties.provider : null),
        rtcSessionId: ctx.rtcSessionId,
        liveId: ctx.liveId ?? null,
        roomId: ctx.roomId ?? null,
        role: ctx.role ?? null,
        uid: ctx.uid ?? null,
        // Browser route on web; native has no URL — the BE stamps platform from
        // the request UA, and the native hook can add a route via properties.
        pageUrl: isWeb() ? window.location.pathname : null,
        timestampUtc: new Date().toISOString(),
        // CORE-1884 — the entry attempt this RTC session belongs to.
        properties: withEntryAttempt(withTelemetryUser(withClientInfo(properties))),
    });
}

function serializeError(err: unknown): Record<string, any> {
    if (err == null) return { message: 'unknown' };
    if (err instanceof Error) {
        return {
            message: err.message,
            name: err.name,
            code: (err as any).code,
            stack: err.stack?.slice(0, 2000),
        };
    }
    if (typeof err === 'object') {
        const obj = err as any;
        try {
            const { message, name, code, stack, ...rest } = obj;
            return {
                message: message ?? String(err),
                name,
                code,
                extra: Object.keys(rest).length > 0 ? rest : undefined,
                stack: typeof stack === 'string' ? stack.slice(0, 2000) : undefined,
            };
        } catch {
            return { message: String(err) };
        }
    }
    return { message: String(err) };
}

// ── Public API ──────────────────────────────────────────────────────────

export function newRtcSessionId(): string {
    return `rtc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ── Client-known user identity (CORE-434) ───────────────────────────────
// The BE stamps the authoritative UserId column from the request's JWT. But
// last-mile flushes go out via navigator.sendBeacon (tab-hide / unload /
// mobile-background), which CANNOT carry an Authorization header — so those
// batches land unattributed (empty UserId), which is exactly what blocked the
// CORE-433 diagnosis (a host's user_published/user_unpublished rows had no
// UserId, so we couldn't tell which agoraUid was his). Stamp the id into the
// event `properties` (a free-form blob the BE persists as-is) so every event is
// attributable regardless of transport. Kept in lock-step with auth via
// setTelemetryUser (see AuthContext), mirroring setSentryUser.
let currentUserId: string | null = null;

export function setTelemetryUser(userId: string | null): void {
    currentUserId = userId;
}

/** Merge the client-known user id into an event's properties (no-op when logged out). */
export function withTelemetryUser(properties: Record<string, any>): Record<string, any> {
    return currentUserId ? { ...properties, userId: currentUserId } : properties;
}

// ── Native device/OS context (CORE-474) ─────────────────────────────────
// Web events carry a real User-Agent that the BE parses for browser/OS/device.
// Native requests have no meaningful UA, so stamp platform/OS/device into the
// event `properties` (free-form jsonb) instead — mirrors withTelemetryUser.
// Computed once from React Native's Platform (no extra dependency; app version
// would need expo-application and is tracked as remaining sub-scope).
let cachedClientInfo: Record<string, any> | null = null;
function nativeClientInfo(): Record<string, any> {
    if (cachedClientInfo) return cachedClientInfo;
    const c: any = (Platform as any).constants ?? {};
    const info: Record<string, any> = {
        platform: Platform.OS,            // 'ios' | 'android'
        osVersion: `${Platform.Version}`, // iOS "17.2" / Android API level
    };
    if (c.systemName) info.systemName = c.systemName; // iOS
    if (c.Brand) info.deviceBrand = c.Brand;          // Android
    if (c.Model) info.deviceModel = c.Model;          // Android
    cachedClientInfo = info;
    return info;
}

/** Merge native device/OS context into an event's properties (no-op on web,
 *  where the BE derives browser/OS/device from the User-Agent). */
function withClientInfo(properties: Record<string, any>): Record<string, any> {
    if (Platform.OS === 'web') return properties;
    return { ...properties, client: nativeClientInfo() };
}

/** Tag the context as "active" so global JS errors get attached to it. */
export function registerRtcContext(ctx: RtcContext): () => void {
    installErrorListeners();
    activeContexts.add(ctx);
    return () => { activeContexts.delete(ctx); };
}

export function trackRtc(eventName: string, ctx: RtcContext, props?: Record<string, any>) {
    emit(eventName, ctx, props ?? {});
}

export function trackRtcError(eventName: string, ctx: RtcContext, err: unknown, props?: Record<string, any>) {
    emit(eventName, ctx, { ...(props ?? {}), error: serializeError(err) });
}
